import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  MenuItem,
  TextareaAutosize,
  TextField,
  Theme
} from "@mui/material";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { SelectAutocomplete } from "components/forms";
import { useFormik } from "formik";
import { useActionHook } from "hooks";
import { useUserActions } from "store";
import { IUsersContact } from "store/types";

import { IRole } from "../../../../store/types/user";
import { AccessForm } from "../Access-three/AccessForm";
import { useShareContext } from "../context/share-context";

import "./InviteBox.styles.scss";

const excludeRoles: number[] = [1, 4, 6, 7];

type multipleShare = {
  sharedUsers: IUsersContactWithSearch[];
  role: IRole;
};

type highlightMatch = {
  text: string;
  highlight: boolean;
};
interface IUsersContactWithSearch extends IUsersContact {
  search: string;
}

export const InviteBox: React.FC = () => {
  const { roles, createShare, createGlobalDocumentShare, switchStep, state, globalDoc, dealId, loading, cb } =
    useShareContext();

  const [inputValue, setInputValue] = useState<string>("");
  const { getUsersContactAction } = useUserActions();
  const { action: getUsers, data: usersRes } = useActionHook(getUsersContactAction);

  useEffect(() => {
    getUsers();
  }, []);

  const usersList: IUsersContactWithSearch[] =
    usersRes?.map((user: IUsersContact) => ({
      search: user.email + user.full_name,
      ...user
    })) || [];

  const minimumRoles: IRole[] = useMemo(() => {
    return roles.filter((item) => !excludeRoles.includes(item.id));
  }, [roles]);

  const initialValues: multipleShare = useMemo(() => {
    return {
      sharedUsers: [],
      role: minimumRoles[0]
    };
  }, [minimumRoles]);

  const { values, setFieldValue, handleSubmit, dirty, isValid, errors } = useFormik({
    validateOnBlur: true,
    initialValues: initialValues,
    onSubmit: async (values: multipleShare, { resetForm }) => {
      const usersEmails = values.sharedUsers.map((user) => (typeof user === "object" ? user.email : user));
      const roleId = values.role.id;

      if (!values.sharedUsers.length) return;

      try {
        if (globalDoc) {
          await createGlobalDocumentShare({
            role_id: roleId,
            emails: usersEmails,
            message: inputValue
          });
        } else {
          if (!dealId) return;
          await createShare({
            dealId: dealId,
            roleId,
            emails: usersEmails,
            message: inputValue,
            share: state.share
          });
        }

        resetForm();
        setFieldValue("sharedUsers", []);
        setInputValue("");
        cb?.();
      } catch (error) {
        throw error;
      }
    }
  });

  return (
    <Container
      sx={{ padding: "16px 0px 0px 0px !important", display: "flex", marginBottom: "30px", gap: "20px" }}
      // maxWidth={false}
    >
      <div className="invite-box">
        <div className="invite-box__main">
          <Box sx={{ display: "flex", gap: "18px" }}>
            <SelectAutocomplete<IUsersContactWithSearch, true, true, true>
              noOptionsText="User not found"
              limitTags={2}
              multiple
              freeSolo
              ListboxProps={{ sx: { height: "800px" } }}
              clearOnBlur={false}
              autoSelect
              value={values.sharedUsers} // Use controlled input
              setValue={(value) => setFieldValue("sharedUsers", value)}
              // defaultValue={values.sharedUsers}
              // onChange={(value) => setFieldValue("sharedUsers", value)}
              compareParam={(option) => option.search}
              getOptionLabel={(value) => {
                return typeof value === "object" ? value.full_name : value;
                // return value?.full_name + " " + value?.email;
              }}
              renderTags={(value, getTagProps) => {
                const chip = value.map((user, index) => {
                  return (
                    <>
                      <Chip
                        {...getTagProps({ index })}
                        key={user.email}
                        label={typeof user === "object" ? user.full_name : user}
                        sx={{ backgroundColor: "transparent", fontSize: "16px" }}
                      />
                      <div>,</div>
                    </>
                  );
                });
                return chip;
              }}
              sx={{
                backgroundColor: "white",
                ".MuiButtonBase-root": {
                  ".MuiChip-root": {
                    backgroundColor: "transparent"
                  }
                },
                ".MuiInputBase-sizeSmall": {
                  padding: values.sharedUsers.length ? "3px !important" : "8px !important"
                }
              }}
              options={usersList || []}
              renderOption={(
                props: React.HTMLAttributes<HTMLLIElement>,
                option: IUsersContactWithSearch,
                { inputValue }
              ) => {
                const matchesName = match(option.full_name, inputValue, { insideWords: true });
                const partsName = parse(option.full_name, matchesName);
                const matchesEmail = match(option.email, inputValue, { insideWords: true });
                const partsEmail = parse(option.email, matchesEmail);

                return (
                  <li {...props} key={option.email}>
                    <img className="invite-box__avatar" src={option.profile_photo_url} alt="" />
                    <div>
                      <p>
                        {partsName.map((part: highlightMatch, index: number) => (
                          <span
                            key={part.text + index}
                            style={{
                              fontWeight: part.highlight ? 700 : 400
                            }}
                          >
                            {part.text}
                          </span>
                        ))}
                      </p>
                      <p>
                        {partsEmail.map((part: highlightMatch, index: number) => (
                          <span
                            key={part.text + index}
                            style={{
                              fontWeight: part.highlight ? 700 : 400
                            }}
                          >
                            {part.text}
                          </span>
                        ))}
                      </p>
                    </div>
                  </li>
                );
              }}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    placeholder={values.sharedUsers.length ? "" : "Add User or Group"}
                    value={values.sharedUsers}
                    error={!!errors.sharedUsers}
                    // helperText={errors.sharedUsers}
                    sx={{ width: "353px" }}
                  />
                );
              }}
            />
            <Box
              component="form"
              sx={{ "& .MuiTextField-root": { m: 1, width: "116px", margin: "0px" } }}
              noValidate
              autoComplete="off"
              className="share-deal__dropdown"
            >
              <TextField
                id="share-deal__dropdown"
                select
                margin="none"
                size="small"
                value={values.role.id}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const selectedRoleId = parseInt(event.target.value, 10);
                  const selectedRole: IRole | undefined = minimumRoles.find((role) => role.id === selectedRoleId);
                  setFieldValue("role", selectedRole);
                }}
              >
                {minimumRoles.map((role) => (
                  <MenuItem className="share-deal__dropdown_options" key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
          <Box sx={{ width: "100%" }}>
            <TextareaAutosize
              placeholder="Message"
              className="textarea"
              value={inputValue}
              onChange={(event) => {
                setInputValue(event.target.value);
              }}
            />
          </Box>
          <Box component="form" onSubmit={handleSubmit}>
            <Button
              type="submit"
              variant="contained"
              disabled={
                !dirty &&
                (!values.sharedUsers.every((user) =>
                  typeof user === "object" ? Boolean(user.full_name?.length) : Boolean(user)
                ) ||
                  !isValid)
              }
              sx={{
                minWidth: 130,
                padding: "0px 4px",
                minHeight: "30px",
                height: "30px",
                backgroundColor: "#0a3142",
                color: "white",
                "&:hover": {
                  backgroundColor: "#0a3142"
                },
                "&.Mui-disabled": {
                  backgroundColor: (theme: Theme) => theme.palette.action.disabledBackground,
                  color: (theme: Theme) => theme.palette.action.disabled
                }
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Invite"}
            </Button>
          </Box>
        </div>
        <Box onClick={() => switchStep("members")} className="scale-click" sx={{ color: "#26a6ae" }}>
          Show Workspace Members
        </Box>
      </div>
      {dealId && (
        <>
          <Divider orientation="vertical" flexItem sx={{ borderBottomWidth: "1px", borderColor: "#dae0e2" }} />
          <AccessForm />
        </>
      )}
    </Container>
  );
};
